import { fieldToScreen, screenToField } from '../components/playbook/fieldCoordinates';
import { VISUAL_RADIUS } from '../components/playbook/PlaybookMarker';

// Shared marker-id + Formation-editor collision-guard helpers — used by
// BOTH the Formation/Front/Unit editor (TeamPlaybookPage.js) and the
// Play starting-alignment editor (TeamPlaybookPlayEditor.js, Phase B.0),
// extracted here so the two never drift into two different collision
// implementations. This is editor INTERACTION behavior only — nothing
// here touches the database, and nothing here is a persisted/global
// collision rule. A future Post-Snap/PTP phase remains completely free
// to let paths/players intersect on purpose (route crossings, blocks,
// chips, double teams, contact); this guard exists solely to stop a
// coach from accidentally stacking markers while placing a STARTING
// alignment.

export function newMarkerId() {
  return `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

// Two markers may touch at their visible edges but not overlap —
// touching (distance exactly equal to this) is allowed. Screen-space
// (viewBox units), derived from the same VISUAL_RADIUS PlaybookMarker.js
// actually draws, so this can never silently drift from the real
// on-screen marker size.
export const MIN_MARKER_SEPARATION = 2 * VISUAL_RADIUS;

/** Given a candidate field-space position for the marker being
 * dragged/spawned, push it back to the nearest non-overlapping screen
 * position against every OTHER marker in `markers` it currently
 * violates the minimum separation against — small and deterministic
 * (one sequential pass, not a physics solver): only the one marker
 * moves, nothing else is rearranged, nothing snaps to a predefined
 * spot. `markers` should be the marker array for ONE side/unit only —
 * callers editing two independent marker sets (e.g. a Play's "our" vs
 * "opponent" starting alignment) call this once per side, never mixed,
 * since the two sides are never meant to collide with each other (they
 * stay on opposite sides of the LOS boundary already). */
export function resolveMarkerCollisions(fieldConfig, draggedId, candidateField, markers) {
  let { px, py } = fieldToScreen(fieldConfig, candidateField.x, candidateField.y);
  for (const other of markers) {
    if (other.id === draggedId) continue;
    const { px: opx, py: opy } = fieldToScreen(fieldConfig, other.start.x, other.start.y);
    const dx = px - opx;
    const dy = py - opy;
    const dist = Math.hypot(dx, dy);
    if (dist < MIN_MARKER_SEPARATION) {
      if (dist === 0) {
        // Degenerate exact-overlap case (e.g. legacy data) — nudge along
        // a fixed axis so the marker isn't stuck exactly on top of another.
        px = opx + MIN_MARKER_SEPARATION;
        py = opy;
      } else {
        const scale = MIN_MARKER_SEPARATION / dist;
        px = opx + dx * scale;
        py = opy + dy * scale;
      }
    }
  }
  return screenToField(fieldConfig, px, py);
}
